import React, { ReactNode, useState } from 'react';
import { ExtractProps } from 'app/utils/types';
import { useCss } from 'app/utils/css';
import { useI18n } from 'app/components/localization';
import Eye from 'app/components/svgs/Eye';
import { StackedLabel, SeparableLabel } from './Labels';
import { BLUE_F, GREY_G, GREY_R, GREY_S, RED_G } from 'app/constants/Colors';
import { buttonReset } from 'app/constants/Styles';
import { inputBaseStyles } from './styles';
import HoverState from '@engoo/react-hover-state';
import BaseButton from 'app/components/common/BaseButton';
import Checkbox from 'app/components/presentation/forms/Checkbox';
import CheckboxWithLabel from 'app/components/presentation/forms/CheckboxWithLabel';
import DateInput from 'app/components/presentation/forms/DateInput';
import Input from 'app/components/presentation/forms/Input';
import Select from 'app/components/presentation/forms/Select';
import ValidationError from 'app/components/presentation/forms/ValidationError';
import Focus from 'app/components/forms/Focus';
import { PasswordRulesContext } from 'app/components/auth/PasswordRules';
import UniqueId from 'app/components/common/UniqueId';
import Tooltip from 'app/components/utilities/Tooltip';

interface IFieldProps {
  label: ReactNode;
  description?: ReactNode;
  validationError?: ReactNode;
}

export const Description: React.FunctionComponent<{
  description?: ReactNode,
  id: string,
}> = ({ description, id }) => {
  const css = useCss();

  if (!description) {
    return null;
  }

  return (
    <div
      data-testid="description"
      id={id}
      className={css('description', {
        marginTop: '6px',
        fontSize: '12px',
        color: GREY_S,
      })}
    >
      {description}
    </div>
  );
};

export const SelectField: React.SFC<IFieldProps & ExtractProps<typeof Select>> = ({
  validationError,
  label,
  ...rest
}) => (
  <SeparableLabel label={label}>{(inputId, labelId, labelRef) =>
    <ValidationError message={validationError}>
      <Select error={!!validationError} labelId={labelId} labelRef={labelRef} {...rest} />
    </ValidationError>
  }</SeparableLabel>
);

export const InputField: React.SFC<IFieldProps & ExtractProps<typeof Input>> = ({
  validationError,
  label,
  description,
  required,
  ...rest
}) => (
  <UniqueId hint="description">{(descriptionId: string) =>
    <>
      <StackedLabel label={label} optional={!required}>
        <ValidationError message={validationError}>
          <Input
            error={!!validationError}
            required={required}
            aria-describedby={description === undefined ? undefined : descriptionId}
            {...rest}
          />
        </ValidationError>
      </StackedLabel>

      <Description id={descriptionId} description={description} />
    </>
  }</UniqueId>
);

export const DateField: React.SFC<IFieldProps & ExtractProps<typeof DateInput>> = ({
  validationError,
  label,
  required,
  ...rest
}) => (
  <StackedLabel label={label} optional={!required}>
    <ValidationError message={validationError}>
      <DateInput error={!!validationError} required={required} {...rest} />
    </ValidationError>
  </StackedLabel>
);

export const CheckboxField: React.SFC<IFieldProps & ExtractProps<typeof Checkbox>> = ({
  validationError,
  label,
  description,
  ...rest
}) => (
  <UniqueId hint="description">{(descriptionId: string) =>
    <ValidationError message={validationError}>
      <CheckboxWithLabel
        checkbox={
          <Checkbox
            error={!!validationError}
            aria-describedby={description === undefined ? undefined : descriptionId}
            {...rest}
          />
        }
      >
        {label}

        <Description id={descriptionId} description={description} />
      </CheckboxWithLabel>
    </ValidationError>
  }</UniqueId>
);

export const PasswordField: React.FunctionComponent<{
  value: string | undefined,
  onChange: (value: string) => void,
  label: ReactNode,
  validationError?: ReactNode,
  required?: boolean,
}> = ({ value, onChange, label, validationError, required }) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowPassword(!showPassword);
  };

  const css = useCss();

  const { t } = useI18n();
  const tooltip = showPassword
    ? t('auth.login-form.hide-password')
    : t('auth.login-form.show-password');

  return (
    <SeparableLabel label={label}>{(inputId) =>
      <PasswordRulesContext.Consumer>{({ fullRule }) =>
        <ValidationError message={<span>{validationError}</span>}>
          {/* If we only have one input and simply change the type value,
              some password managers will not ask to save the value (because
              there is no password field). Therefore, the password field
              is always included and prevented from displaying by using CSS
              display, which still keeps it in the DOM. */}
          <Focus>{({ hasFocus, onFocus, onBlur }) =>
            <div
              className={css('passwordContainer', [
                {
                  ...inputBaseStyles,
                  display: 'flex',
                  flexGrow: 1,
                },

                hasFocus && {
                  outline: 'none',
                  border: `1px solid ${GREY_S}`,
                },

                !!validationError && {
                  borderColor: RED_G,
                },
              ])}
            >
              {showPassword && <Input
                id={inputId}
                type="text"
                value={value || ''}
                onChange={onChange}
                error={!!validationError}
                required={required}
                style={{ border: 'none' }}
                onFocus={onFocus}
                onBlur={onBlur}
              />}
              <Input
                id={showPassword ? '' : inputId}
                type="password"
                value={value || ''}
                onChange={onChange}
                error={!!validationError}
                style={{ display: showPassword ? 'none' : undefined, border: 'none' }}
                required={required}
                onFocus={onFocus}
                onBlur={onBlur}
                passwordrules={fullRule}
              />
              <Tooltip title={tooltip}>
                <HoverState>{(isHovered) => (
                  <BaseButton
                    type="button"
                    className={css('showPasswordButton', {
                      ...buttonReset,
                      width: '30px',
                      minWidth: '30px',
                      height: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fill: (
                        showPassword ? BLUE_F :
                        isHovered ? GREY_G :
                        /* else */ GREY_R
                      ),
                    })}
                    onClick={toggleShowPassword}
                    aria-label={tooltip}
                  >
                    <Eye
                      className={css('showPasswordIcon', {
                        height: '1.2em',
                        width: 'auto',
                        stroke: 'none',
                      })}
                    />
                  </BaseButton>
                )}</HoverState>
              </Tooltip>
            </div>
          }</Focus>
        </ValidationError>
      }</PasswordRulesContext.Consumer>
    }</SeparableLabel>
  );
};
